import { Box, Button, Stack, TextField } from '@mui/material';
import { useState } from 'react';

import { useBoardEditingStore, type WallBoard, type WallCard } from '@walls/store';
import { useCardCommands } from '../cards/actions/useCardCommands';
import { isClusterCard } from '../selectors/boardCardSelectors';
import { useClusterCommands } from '../clusters/hooks/useClusterCommands';
import { CardActionPanel } from './CardActionPanel';
import { CardColorPicker } from './CardColorPicker';
import { useFocusTextFieldAtEnd } from './hooks/useFocusTextFieldAtEnd';
import { RichTextCardEditor } from './RichTextCardEditor';
import { boardCardColors } from './utils/cardColors';
import { containsHtml, sanitizeEditorRichText, stripHtml, toRichTextHtml } from './utils/richText';

interface EditTextCardDialogProps {
  board: WallBoard;
  card: WallCard;
}

export function EditTextCardDialog({ board, card }: EditTextCardDialogProps) {
  const stopEditing = useBoardEditingStore((state) => state.stopEditing);
  const cardCommands = useCardCommands(board);
  const clusterCommands = useClusterCommands(board);
  const textField = useFocusTextFieldAtEnd([card.id]);
  const isCluster = isClusterCard(card);
  const initialContent = isCluster ? card.title?.trim() || card.content || '' : card.content ?? '';

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [content, setContent] = useState(initialContent);
  const [color, setColor] = useState(card.color ?? '#ffff99');
  const [richEditing, setRichEditing] = useState(containsHtml(initialContent));
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    if (!isSaving) {
      stopEditing();
    }
  };

  const save = async () => {
    const trimmedContent = richEditing ? sanitizeEditorRichText(content) : content.trim();

    if (!stripHtml(trimmedContent) || isSaving) {
      close();
      return;
    }

    setIsSaving(true);

    try {
      if (isCluster) {
        await clusterCommands.updateCluster(card.id, stripHtml(trimmedContent), '', color);
      } else {
        await cardCommands.updateTextCard(card.id, trimmedContent, color);
      }

      stopEditing();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CardActionPanel
      backgroundColor={color}
      onClose={close}
      onClick={() => setShowColorPicker(false)}
    >
      {richEditing ? (
        <RichTextCardEditor initialValue={content} onChange={setContent} />
      ) : (
        <TextField
          inputRef={textField.inputRef}
          multiline
          rows={5}
          value={content}
          onFocus={textField.onFocus}
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void save();
            }
          }}
          sx={{
            height: 260,
            '& .MuiOutlinedInput-root': {
              alignItems: 'flex-start',
              height: '100%',
              '& fieldset': {
                border: 'none',
              },
              '&:hover fieldset': {
                border: 'none',
              },
              '&.Mui-focused fieldset': {
                border: 'none',
              },

              '& .MuiInputBase-input': {
                boxSizing: 'border-box',
                fontSize: '36px',
                height: '100% !important',
                lineHeight: 1.2,
                overflowY: 'auto !important',
                resize: 'none',
              },
            },
          }}
        />
      )}
      <Stack
        direction="row"
        justifyContent={!richEditing ? 'space-between' : 'flex-end'}
        alignItems="end"
        spacing={2}
      >
        {!richEditing && (
          <Button
            variant="text"
            onClick={() => {
              setContent(toRichTextHtml(content));
              setRichEditing(true);
            }}
          >
            More
          </Button>
        )}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
            onClick={(event) => event.stopPropagation()}
          >
            <Box
              component="button"
              type="button"
              aria-label={`Use color ${color}`}
              onClick={() => setShowColorPicker(!showColorPicker)}
              sx={{
                bgcolor: color,
                border: '1px solid #d0d5dd',
                borderRadius: '50%',
                boxShadow: color === '#ffffff' ? 'inset 0 0 0 1px #e4e7ec' : undefined,
                cursor: 'pointer',
                height: 30,
                width: 30,
                p: 0,
              }}
            />
            {showColorPicker && (
              <CardColorPicker
                colors={boardCardColors}
                value={color}
                onChange={(nextColor) => {
                  setColor(nextColor);
                  setShowColorPicker(false);
                }}
              />
            )}
          </Box>
          <Button variant="contained" onClick={() => void save()} disabled={isSaving}>
            Done
          </Button>
        </Stack>
      </Stack>
    </CardActionPanel>
  );
}
